import axios, { AxiosRequestConfig } from 'axios'
import { get } from 'lodash'
import {
  getPaginationParams,
  GetParamsInterface,
  PaginationInterface,
} from '@bit/jimmijoensson.keyhole.interfaces.paginated-response-interface'

export const setAsyncTimeout = async (ms: number) => {
  return new Promise(resolve => {
    setTimeout(resolve, ms)
  })
}

export const isStringifyEqual = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b)

export const isEmptyStr = (str?: string | null) =>
  str === '00000000-0000-0000-0000-000000000000' ||
  str === '' ||
  str === null ||
  str === undefined ||
  str === '1970-01-01T00:00:00Z'

export const openInNewTab = (url: string) => {
  const win = window.open(url, '_blank')
  if (win !== null) win.focus()
}

export const capitalize = (s: string) => {
  if (typeof s !== 'string') return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export const sortBy = (
  array: any,
  property: string,
  dataType: 'date' | 'string',
  order: 'asc' | 'desc',
  prioritizeString?: string
) => {
  let sortedArray
  if (dataType === 'date') {
    sortedArray = array.sort((a: any, b: any) => {
      a = new Date(get(a, property))
      b = new Date(get(b, property))
      return (order === 'asc' && a - b) || (order === 'desc' && b - a) || -1
    })
  } else {
    const collator = new Intl.Collator('da-DK', { numeric: true, sensitivity: 'base' })
    sortedArray = array.sort((a: any, b: any) => {
      const aProperty = get(a, property)
      const bProperty = get(b, property)
      return collator.compare(
        prioritizeString === aProperty ? '' : aProperty,
        prioritizeString === bProperty ? '' : bProperty
      )
    })
    sortedArray = order === 'asc' ? sortedArray : sortedArray.reverse()
  }

  return sortedArray
}

export const getObjectKeys = <O extends object>(obj: O) => Object.keys(obj) as Array<keyof O>

export const uuid = () => {
  const url = URL.createObjectURL(new Blob())
  const [id] = url
    .toString()
    .split('/')
    .reverse()
  URL.revokeObjectURL(url)
  return id
}

export const addDifferenceToValue = (oldVal: number, newVal: number, valToIncrease: number) =>
  valToIncrease + (newVal - oldVal)

export const reOrder = (order: 'asc' | 'desc' | undefined) => (order === 'asc' ? 'desc' : 'asc')

export const nextPaginatedParams = (pagination: PaginationInterface) =>
  ({
    ...getPaginationParams(pagination),
    offset:
      pagination.offset !== undefined && pagination.limit !== undefined
        ? pagination.offset + pagination.limit
        : 0,
  } as GetParamsInterface)

export const sortParams: any = (
  orderBy: PaginationInterface['orderBy'],
  params: PaginationInterface
) => ({
  ...params,
  offset: 0,
  order: params.orderBy === orderBy ? reOrder(params.order) : 'asc',
  orderBy: orderBy,
})

export interface DaysRange {
  amount: number
  title?: string
}

export const setAsyncData = async (
  setter: React.Dispatch<React.SetStateAction<any>>,
  getter: () => Promise<any>
) => {
  setter(await getter())
}

export const getNonDecimalEnding = (locale?: string, number?: number) => {
  if (number && !Number.isInteger(number)) return ''
  switch (locale) {
    case 'da-DK':
      return ',-'

    default:
      return '.-'
  }
}

export const toNonDecimalLocaleString = (
  number: number,
  locale?: string | undefined,
  options?: Intl.NumberFormatOptions | undefined
) => {
  const isInteger = Number.isInteger(number)
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2, ...options }).format(number) + (
    isInteger
    ? getNonDecimalEnding(locale)
    : ''
  )
}

export const pickObjProps = <T, K>(obj: any, fields: K[]) =>
  fields.reduce(
    (acc, k: any) => ({ ...acc, ...(obj.hasOwnProperty(k) && { [k]: obj[k] }) }),
    {}
  ) as T

const resources: any = {}

export const makeRequestCreator = () => {
  let cancel: any

  return async (config: AxiosRequestConfig, cached?: boolean) => {
    if (cancel && cached) {
      // Cancel the previous request before making a new request
      cancel.cancel()
    }
    const cacheKey = config.url + new URLSearchParams(config.params).toString()
    // Create a new CancelToken
    cancel = axios.CancelToken.source()
    try {
      if (cached && resources[cacheKey]) {
        // Return result if it exists
        return resources[cacheKey]
      }
      const res = await axios({
        ...config,
        cancelToken: cancel.token,
      })

      const result = res.data
      // Store response
      resources[cacheKey] = result

      return result
    } catch (error) {
      if (axios.isCancel(error)) {
        // Handle if request was cancelled
      } else {
        // Handle usual errors
        throw error
      }
    }
  }
}

export const request = makeRequestCreator()
